import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  from,
  HttpLink,
  InMemoryCache,
  Observable,
  ServerError,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Auth } from 'aws-amplify';
import { createClient as createWsClient } from 'graphql-ws';

const wsLink = new GraphQLWsLink(
  createWsClient({
    url: process.env.REACT_APP_GRAPHQL_SOCKET_URI,
    connectionParams: async () => {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      return {
        Authorization: token,
      };
    },
  }),
);

// don't send queries unless authenticated
let sendQueries = false;

const httpLink = new HttpLink({ uri: process.env.REACT_APP_GRAPHQL_URI });

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  split(() => sendQueries, httpLink),
);

type handleErrorType = (message: string, code?: string) => any;
class AuthMiddlewareLink extends ApolloLink {
  request(operation, forward) {
    return new Observable((observer) => {
      let handle;
      void (async () => {
        try {
          // Use Amplify's Auth module to retrieve the token asynchronously
          const token = await Auth.currentSession().then((data) => data.getIdToken().getJwtToken());

          // add the authorization to the headers
          operation.setContext({
            headers: {
              authorization: token || null,
            },
          });

          // Proceed with the request chain
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        } catch (error) {
          // Handle any error that might occur during the token retrieval
          console.error('Error while fetching token:', error);
        }
      })();
      // Cleanup function to unsubscribe from the request chain
      return () => {
        if (handle) {
          handle.unsubscribe();
        }
      };
    });
  }
}

export const refreshToken = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const currentSession = user.signInUserSession;
    user.refreshSession(currentSession.refreshToken, (error) => {
      if (error) {
        console.error('Error while refreshing token:', error);
      }
    });
  } catch (error) {
    console.error('Error while refreshing token:', error);
  }
};

export const createErrorLink = (handleError: handleErrorType) => {
  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    graphQLErrors?.forEach(({ message, extensions }) => {
      handleError(message, extensions.code as string);
    });

    if ((networkError as ServerError)?.statusCode === 401) {
      const observable = new Observable<FetchResult<Record<string, any>>>((observer) => {
        void (async () => {
          await refreshToken();
          // Retry the failed request
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };

          forward(operation).subscribe(subscriber);
        })();
      });

      return observable;
    }

    return null;
  });
};

export const createClient = (onError: handleErrorType, isAuthenticated: boolean) => {
  const errorLink = createErrorLink(onError);
  const links = [errorLink];
  sendQueries = isAuthenticated;
  links.push(new AuthMiddlewareLink());
  links.push(link);
  return new ApolloClient({
    link: from(links),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            organization: {
              merge(existing = {}, incoming: any) {
                return { ...existing, ...incoming };
              },
            },
            segments: {
              keyArgs: ['searchString', 'filters'],
              merge(existing, incoming, { args }) {
                if (!existing) return incoming;
                if (!args?.after) return incoming;

                // Merge paginated results - deduplicate by segment ID
                const existingNodes = (existing.nodes || []) as Array<{ id: string }>;
                const incomingNodes = (incoming.nodes || []) as Array<{ id: string }>;
                const existingIds = new Set(existingNodes.map((node) => node.id));
                const newNodes = incomingNodes.filter((node) => !existingIds.has(node.id));

                return {
                  ...incoming,
                  nodes: [...existingNodes, ...newNodes],
                };
              },
            },
            vehiclesSnapshot: {
              keyArgs: ['filters', 'sort', 'searchQuery', 'dateRange', 'country', 'getListings'],
              merge(existing, incoming, { args }) {
                if (!existing) return incoming;
                if (!args?.after) return incoming;

                // Merge paginated results - deduplicate by vehicle VIN
                const existingNodes = (existing.nodes || []) as Array<{ vin: string }>;
                const incomingNodes = (incoming.nodes || []) as Array<{ vin: string }>;
                const existingVins = new Set(existingNodes.map((node) => node.vin));
                const newNodes = incomingNodes.filter((node) => !existingVins.has(node.vin));

                return {
                  ...incoming,
                  nodes: [...existingNodes, ...newNodes],
                };
              },
            },
            fieldCounts: {
              keyArgs: ['propertyName', 'dateRange', 'filters', 'searchQuery', 'country'],
              merge(existing, incoming, { args }) {
                if (!existing) return incoming;
                if (!args?.after) return incoming;

                // Merge paginated results - deduplicate by key
                const existingNodes = (existing.nodes || []) as Array<{ key: string }>;
                const incomingNodes = (incoming.nodes || []) as Array<{ key: string }>;
                const existingKeys = new Set(existingNodes.map((node) => node.key));
                const newNodes = incomingNodes.filter((node) => !existingKeys.has(node.key));

                return {
                  ...incoming,
                  nodes: [...existingNodes, ...newNodes],
                };
              },
            },
          },
        },
      },
    }),
    devtools: {
      enabled: process.env.REACT_APP_APOLLO_DEVTOOLS === 'true',
    },
  });
};
